// Boundary module: acquires a delegated Microsoft Graph access token via
// Nested App Authentication (NAA). This replaces the deprecated
// getCallbackTokenAsync / EWS callback-token path on Exchange Online.
// See docs/adr/ADR-001-naa-graph-replaces-ews-callback-tokens.md.

// Type-only: erased at compile time, so it costs nothing in the bundle. The
// runtime module is pulled in lazily by getMsalInstance() below.
import { type IPublicClientApplication } from "@azure/msal-browser";
import { debugLog } from "./debug";

// Baseline delegated scopes requested by the connect flow (detectAdapter's
// silent check at mount, connectGraphAdapter's interactive popup). Mail.Read
// covers list/search/get-body; User.Read is required so the token request
// includes a scope beyond openid/profile/email. Keeping this minimal means a
// tenant/app registration that has only granted these two scopes still gets
// a working "Connect" experience — see
// docs/superpowers/plans/2026-07-02-gap-1-grounding-calendar-attachments.md
// and docs/superpowers/plans/2026-07-02-gap-4-triage-auto-categorization.md
// for why the elevated scopes below are requested on demand instead.
export const BASELINE_SCOPES = ["Mail.Read", "User.Read"];

/**
 * Registry of Graph delegated scopes beyond the connect-time baseline.
 * Each feature scope is acquired lazily, at the point of use, via
 * {@link ensureGraphToken} — this is incremental consent: connecting an
 * account never requires more than {@link BASELINE_SCOPES}, and an elevated
 * operation degrades with an actionable "needs consent" error instead of
 * silently failing the whole connect flow when admin/tenant consent for the
 * wider scope hasn't been granted yet.
 */
export const FEATURE_SCOPES = {
  /** Mailbox writes: move message between folders, write categories. */
  mailReadWrite: ["Mail.ReadWrite"],
  /** Read-only calendar grounding (list events for `[[calendar]]` context). */
  calendarsRead: ["Calendars.Read"],
  /**
   * Calendar writes: create a new event, respond to a meeting invite
   * (accept/decline/tentatively accept). Additive to `calendarsRead` — the
   * read-only scope keeps covering `listCalendarEvents` grounding; this wider
   * scope is only requested lazily, at the point a write tool is invoked
   * (`createEvent`/`respondToEvent`, mailbox-adapter.ts).
   */
  calendarsReadWrite: ["Calendars.ReadWrite"],
  /** Outlook categories / masterCategories management — not yet used. */
  mailboxSettingsReadWrite: ["MailboxSettings.ReadWrite"],
  /**
   * Purview retention: assigning a label to a message writes the MAPI
   * PolicyTag extended property via `PATCH /me/messages/{id}`, which needs
   * `Mail.ReadWrite` in addition to the records-management scope — see
   * `applyRetentionLabel` (mailbox-adapter.ts).
   */
  recordsManagementReadWriteAll: ["RecordsManagement.ReadWrite.All"],
  /**
   * Purview retention: least-privilege read scope for listing the org's
   * retention label definitions (`GET /security/labels/retentionLabels`) —
   * least privilege (CLAUDE.md A5): listing never writes, so it must not
   * request the read-write scope.
   */
  recordsManagementReadAll: ["RecordsManagement.Read.All"],
  /**
   * One-way sync of local follow-up tasks to Microsoft To Do (create/update/
   * delete a task in the dedicated "AI Assistant" list) — see
   * `createRemoteTask`/`updateRemoteTask`/`deleteRemoteTask` (mailbox-adapter.ts).
   */
  tasksReadWrite: ["Tasks.ReadWrite"],
} as const satisfies Record<string, string[]>;

// `common` supports work/school and personal accounts. A multi-tenant app
// registration means one registration serves every tenant.
const AUTHORITY = "https://login.microsoftonline.com/common";

// Marker surfaced to callers (and, transitively, the UI) when an elevated
// scope has not been consented to yet. Exported so a caller can distinguish
// "needs consent" from any other failure without inspecting MSAL internals.
export const NEEDS_CONSENT = "NEEDS_CONSENT";

/**
 * Thrown by callers (e.g. `GraphAdapter`) when an elevated Graph scope could
 * not be acquired silently. Never wraps or logs the raw MSAL error — only
 * `error.name` is ever recorded, via `debugLog`, per CLAUDE.md A3/A5 (no
 * secrets/PII in logs).
 *
 * Carries the specific `scopes` that were missing for the attempted
 * operation (e.g. just `MailboxSettings.ReadWrite` for a label tool, not the
 * full feature-scope bundle) so a re-consent popup can request only what
 * that operation needs — see `elevateGraphAdapter` (mailbox-adapter.ts). This
 * keeps a non-admin user's "Grant permission" click from being blocked by an
 * unrelated admin-consent-only scope (e.g. `RecordsManagement.ReadWrite.All`)
 * bundled into the same popup.
 */
export class GraphConsentRequiredError extends Error {
  readonly code = NEEDS_CONSENT;
  readonly cause?: unknown;
  readonly scopes: string[];
  constructor(message: string, scopes: string[], options?: { cause?: unknown }) {
    super(message);
    // Restores the prototype chain lost when TS downlevels classes extending
    // built-in Error to the tsconfig `target: "es5"` used by the production
    // (ts-loader) build — without this, `instanceof GraphConsentRequiredError`
    // silently returns false in dist/, even though it passes under Vitest
    // (which doesn't downlevel to ES5). See TS handbook "Extending built-ins".
    Object.setPrototypeOf(this, GraphConsentRequiredError.prototype);
    this.name = "GraphConsentRequiredError";
    this.scopes = scopes;
    this.cause = options?.cause;
  }
}

let msalInstance: IPublicClientApplication | null = null;
let cachedClientId: string | null = null;

/**
 * Builds (and memoizes) the NAA client for `clientId`.
 *
 * `@azure/msal-browser` is ~232 KiB of the task pane bundle and is only needed
 * on the Graph path — a mailbox using the EWS fallback adapter never touches
 * it. Loading it through a dynamic `import()` keeps it in its own chunk,
 * fetched on the first token request rather than at task pane load (CLAUDE.md
 * A10). Callers are already async, so this adds no new await to the API.
 *
 * The clientId check is re-evaluated *after* the await: the module load is an
 * async gap during which a different clientId could have been requested, and
 * handing back an instance built for another app registration would acquire
 * tokens against the wrong tenant.
 */
async function getMsalInstance(clientId: string): Promise<IPublicClientApplication> {
  if (msalInstance && cachedClientId === clientId) {
    return msalInstance;
  }
  const { createNestablePublicClientApplication } = await import("@azure/msal-browser");
  if (msalInstance && cachedClientId === clientId) {
    return msalInstance;
  }
  msalInstance = await createNestablePublicClientApplication({
    auth: { clientId, authority: AUTHORITY },
  });
  cachedClientId = clientId;
  return msalInstance;
}

/**
 * Acquires a Graph token silently (no UI) for the given `scopes` (default:
 * {@link BASELINE_SCOPES}). Returns null on any failure — the caller degrades
 * gracefully; interactive sign-in happens only from a user gesture via
 * {@link getGraphTokenInteractive}.
 */
export async function getGraphTokenSilent(
  clientId: string,
  scopes: string[] = BASELINE_SCOPES
): Promise<string | null> {
  if (!clientId) {
    return null;
  }
  try {
    const pca = await getMsalInstance(clientId);
    const result = await pca.acquireTokenSilent({ scopes });
    debugLog("auth:silent", "token acquired");
    return result.accessToken;
  } catch (error) {
    debugLog("auth:silent-failed", error instanceof Error ? error.name : "unknown");
    return null;
  }
}

/**
 * Acquires a Graph token via an interactive popup for the given `scopes`
 * (default: {@link BASELINE_SCOPES}). MUST be called from a synchronous
 * user-gesture handler (e.g. a button click) or the browser blocks the
 * popup. Returns null on blocked/cancelled/declined.
 *
 * Relies on {@link getMsalInstance}'s memoization for warmup: the silent call
 * at mount ({@link getGraphTokenSilent}, via `detectAdapter`) already resolves
 * `createNestablePublicClientApplication` for the configured clientId, so this
 * call reuses the cached instance instead of awaiting construction after the
 * click — keeping the async gap before `acquireTokenPopup` minimal so the
 * popup isn't blocked. If the clientId changes (e.g. updated in Settings),
 * `ChatPanel` remounts and re-runs `detectAdapter`, re-warming the instance
 * before the "Connect" affordance can be clicked again.
 */
export async function getGraphTokenInteractive(
  clientId: string,
  scopes: string[] = BASELINE_SCOPES,
  prompt?: "select_account" | "consent" | "login"
): Promise<string | null> {
  if (!clientId) {
    return null;
  }
  try {
    const pca = await getMsalInstance(clientId);
    const request: { scopes: string[]; prompt?: string } = { scopes };
    // Only set `prompt` when asked — an undefined prompt must not appear in the
    // request (MSAL treats a present-but-undefined prompt differently from an
    // absent one, and existing connect/elevate flows rely on the default).
    if (prompt) {
      request.prompt = prompt;
    }
    const result = await pca.acquireTokenPopup(request);
    debugLog("auth:popup", "token acquired");
    return result.accessToken;
  } catch (error) {
    debugLog("auth:popup-failed", error instanceof Error ? error.name : "unknown");
    return null;
  }
}

/**
 * Best-effort read of the signed-in account's username (email) for display in
 * the UI (header account menu, Settings mailbox section). Never throws and
 * never triggers auth — returns null when no client id, no cached account, or
 * MSAL isn't ready. `username` is shown to the user only, never logged (A5).
 */
export async function getActiveGraphAccountName(clientId: string): Promise<string | null> {
  if (!clientId) {
    return null;
  }
  try {
    const pca = await getMsalInstance(clientId);
    return pca.getAllAccounts()[0]?.username ?? null;
  } catch {
    return null;
  }
}

/**
 * Acquires a token for an arbitrary (typically elevated) scope set without
 * ever popping a sign-in window. Used by `GraphAdapter` to fetch a scope
 * on demand, at the point an elevated operation is invoked: if the account
 * already consented to `scopes` (e.g. in a prior interactive session, or via
 * admin consent), MSAL returns a cached/refreshed token silently; otherwise
 * this resolves null and the caller surfaces a "needs consent" error rather
 * than throwing a raw MSAL error or triggering an unexpected popup outside a
 * user gesture.
 */
export async function ensureGraphToken(clientId: string, scopes: string[]): Promise<string | null> {
  return getGraphTokenSilent(clientId, scopes);
}

/**
 * Best-effort local disconnect. NAA does not support `logout`/`logoutPopup`
 * (they throw), and the host brokers the identity, so a true Entra sign-out
 * isn't possible from the add-in. This clears MSAL's local cache when the API
 * is available and drops the memoized instance so the next acquisition starts
 * fresh — the app-level effect (falling back to the Unavailable adapter) is what
 * the user sees. Never throws.
 */
export async function disconnectGraph(clientId: string): Promise<void> {
  if (!clientId) {
    return;
  }
  try {
    const pca = await getMsalInstance(clientId);
    const maybeClear = (pca as { clearCache?: () => Promise<void> }).clearCache;
    if (typeof maybeClear === "function") {
      await maybeClear.call(pca);
    }
  } catch (error) {
    debugLog("auth:disconnect-failed", error instanceof Error ? error.name : "unknown");
  } finally {
    msalInstance = null;
    cachedClientId = null;
  }
}

/** True when the host supports Nested App Authentication (Exchange Online). */
export function isNaaSupported(): boolean {
  return Office.context.requirements.isSetSupported("NestedAppAuth", "1.1");
}

// Test seam: reset the memoized MSAL instance between unit tests.
export function __resetMsalForTests(): void {
  msalInstance = null;
  cachedClientId = null;
}
